import {
  baseUrlV2,
  getCredentials,
  fetchOptionsV2,
  createQueryParameters,
  fetchPostOptions,
  fetchOptions,
  baseUrl,
} from './http';
import { downloadBlob, displaySuccess } from '../utils';
import { FourOhOneError } from '../actionCreators/system';
import CustomToast from '../components/Redesing/custom-toast';
import { API_PAGINATION_LIMIT } from '../constants';

export const getMemberships = (parameters) => {
  const { adminEmail, adminToken, systemGid } = getCredentials();
  const fetchOps = fetchOptionsV2(adminEmail, adminToken);

  const { system_id } = parameters;

  const actualSystemId = system_id || systemGid;

  const limit = parameters.membership_id
    ? null
    : parameters.limit || API_PAGINATION_LIMIT;

  const queryParametersObject = {
    limit: limit,
    ...(!parameters.membership_id && {
      ...(parameters.organization_id
        ? {
            organization_id: parameters.organization_id,
            ...(system_id && { system_id }),
          }
        : { system_id: actualSystemId }),
    }),
    ...(parameters.membership_id && { membership_id: parameters.membership_id }),
    ...(parameters.lastId && { after: parameters.lastId }),
    ...(parameters.status && { status: parameters.status }),
    ...(parameters.order_by && { order_by: parameters.order_by }),
    ...(parameters.order && { order: parameters.order }),
    ...(parameters.pattern && { pattern: `%${parameters.pattern}%` }),
  };

  const queryParameters = createQueryParameters(queryParametersObject);
  return fetch(`${baseUrlV2}/membership/?${queryParameters}`, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response?.data) {
        return response;
      }
      FourOhOneError(response);
      throw new Error(response?.error_description);
    })
    .catch((e) => {
      CustomToast({
        type: 'error',
        message: `Oops! Looks like there was an error while loading the memberships. Please try again later. ${e}`,
        duration: 5000,
      });
      return [];
    });
};

export const getUserMemberships = (parameters) => {
  const { adminEmail, adminToken, systemGid } = getCredentials();
  const fetchOps = fetchOptionsV2(adminEmail, adminToken);
  // Determine if we're using organization_id
  const { organization_id, system_id, ...restParameters } = parameters;

  const actualSystemId = system_id || systemGid;

  const queryParametersObject = {
    limit: parameters.limit ?? 100,
    order: 'desc',
    ...(organization_id
      ? { organization_id: organization_id }
      : { system_id: actualSystemId }),
    ...restParameters,
  };

  const queryParameters = createQueryParameters(queryParametersObject);

  return fetch(`${baseUrlV2}/membership/user?${queryParameters}`, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response?.data) {
        return response.data;
      }
      FourOhOneError(response);
      throw new Error(response?.error_description);
    })
    .catch((e) => {
      CustomToast({
        type: 'error',
        message: `Oops! Looks like there was an error while loading users memberships. Please try again later. ${e}`,
        duration: 5000,
      });
      return [];
    });
};

export const exportUserMemberships = (parameters) => {
  const { adminEmail, adminToken } = getCredentials();
  const fetchOps = fetchOptionsV2(adminEmail, adminToken);
  const queryParametersObject = {
    ...parameters,
  };
  const queryParameters = createQueryParameters(queryParametersObject);

  return fetch(`${baseUrlV2}/membership/user/export?${queryParameters}`, fetchOps)
    .then((response) => {
      if (!response.ok) {
        throw Error(response.statusText || response.status);
      }
      return response;
    })
    .then((response) => response.blob())
    .then((blob) => downloadBlob(blob, 'membership_users.csv'))
    .then(() => displaySuccess('The membership users have been exported successfully!'))
    .catch((error) => {
      CustomToast({
        type: 'error',
        message: `Oops! An error has occurred while exporting the membership users. Please try again later. ${error}`,
        duration: 5000,
      });
    });
};

export const membershipApproval = (payload, system_id = '') => {
  const { body, userId } = payload;
  const { adminEmail, adminToken, systemGid } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemGid,
    body,
    'PUT'
  );
  return fetch(`${baseUrlV2}/membership/user/${userId}/approve`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.data) {
        return results.data;
      }
      FourOhOneError(results);
      if (results?.error_type === 'payment_error') {
        CustomToast({
          type: 'error',
          message: `There was an error approving the membership: ${results?.error_description}`,
        });
        return null;
      }
      throw new Error(results?.error_description);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const addUserMembership = (payload, system_id = '') => {
  const { adminEmail, adminToken, systemGid } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemGid,
    payload,
    'POST'
  );
  return fetch(`${baseUrlV2}/membership/user`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results?.data) {
        return results.data;
      } else if (results?.error_type === 'duplicate') {
        throw new Error('This user already has this membership active.');
      } else {
        throw new Error(results?.error_description);
      }
    })
    .catch((e) => {
      return Promise.reject(`${e}`);
    });
};

export const getMembershipImages = (params) => {
  const { adminEmail, adminToken, systemId } = getCredentials();

  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    Boolean(params?.organization_id) ? '' : systemId
  );

  const url = new URL(`${baseUrl}/approve-image/images/membership`);
  if (params?.organization_id) {
    url.searchParams.set('organization_id', params?.organization_id);
  }

  return fetch(url, fetchOps)
    .then((response) => response.json())
    .then((results) => {
      if (results.status === 200) {
        return results.message;
      }
      FourOhOneError(results);
      throw new Error(results?.error_description);
    })
    .catch((error) => {
      CustomToast({
        type: 'error',
        message: `There was an error getting images: ${error}`,
        duration: 5000,
      });
    });
};

export const archiveMembership = (membershipId, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    {
      archive: true,
    },
    'PUT'
  );
  return fetch(`${baseUrl.replace('v1', 'v2')}/membership/${membershipId}`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results?.data) {
        displaySuccess('Your membership was archived successfully!');
      } else {
        FourOhOneError(results);
        throw new Error(results?.error_description);
      }
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const cancelMembership = (userMembershipId, reason) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, { reason }, 'PUT');

  return fetch(`${baseUrlV2}/membership/user/${userMembershipId}/cancel`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results?.data) {
        displaySuccess('The user membership was canceled successfully!');
        return results?.data;
      }
      FourOhOneError(results);
      throw new Error(results?.error_description);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const expireMembership = (userMembershipId) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, {}, 'PUT');

  return fetch(`${baseUrlV2}/membership/user/${userMembershipId}/expire`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results?.data) {
        displaySuccess('The user membership was expired successfully!');
        return results?.data;
      }
      FourOhOneError(results);
      throw new Error(
        results?.error_description ||
          results?.message ||
          'Oops! It looks like there was an error on our end while trying to expire the user membership. Please try again later!'
      );
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const uploadImage = (formData, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  return fetch(`${baseUrl}/system/settings/image`, {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `Basic ${btoa(`${adminEmail}:${adminToken}`)}`,
      system: system_id || systemId,
    },
  })
    .then((res) => res.json())
    .then((results) => {
      if (results.status === 200) {
        return results.message;
      }
      FourOhOneError(results);
      throw new Error(results.message);
    })
    .catch((error) => {
      CustomToast({
        type: 'error',
        message: `There was an error uploading the image: ${error}`,
        duration: 5000,
      });
      return null;
    });
};

export const updateMembership = (membershipId, fields, system_id = '') => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    system_id || systemId,
    fields,
    'PUT'
  );
  return fetch(`${baseUrlV2}/membership/${membershipId}`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.data) {
        return results.data;
      }
      FourOhOneError(results);
      throw new Error(results.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const addMembership = (body, system_id = '') => {
  const { adminEmail, adminToken, systemGid } = getCredentials();

  // check if system_id in body is empty, if it is, use systemId
  const actualSystemId = body.system_id || systemGid;

  // if body?.system_id is empty replace with systemId
  if (!body?.system_id) {
    body.system_id = systemGid;
  }

  const fetchOps = fetchPostOptions(adminEmail, adminToken, actualSystemId, body, 'POST');

  return fetch(`${baseUrlV2}/membership/`, fetchOps)
    .then((res) => res.json())
    .then((response) => {
      if (response?.data) {
        return response.data;
      } else {
        FourOhOneError(response);
        throw new Error(response?.error_description);
      }
    })
    .catch((e) => {
      return Promise.reject(`${e}`);
    });
};

export const updateMembershipPerks = (membershipId, fields) => {
  const { adminEmail, adminToken, systemId, systemGid } = getCredentials();
  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, fields, 'PUT');

  const queryParametersObject = {
    system_id: systemGid,
  };

  const queryParameters = createQueryParameters(queryParametersObject);

  return fetch(
    `${baseUrlV2}/membership/${membershipId}/perks?${queryParameters}`,
    fetchOps
  )
    .then((res) => res.json())
    .then((results) => {
      if (results.data) {
        displaySuccess('Your membership was updated successfully');
        return true;
      }
      FourOhOneError(results);
      throw new Error(results.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};

export const addMinutesToMembership = (membershipId, minutes) => {
  const { adminEmail, adminToken, systemId } = getCredentials();

  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, { minutes }, 'PUT');
  return fetch(`${baseUrlV2}/membership/user/${membershipId}/admin/add-minutes`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.data) {
        return true;
      }
      FourOhOneError(results);
      throw new Error(results.error_description);
    })
    .catch((e) => {
      return Promise.reject(`${e}`);
    });
};

export const addUnlocksToMembership = (membershipId, unlocks) => {
  const { adminEmail, adminToken, systemId } = getCredentials();

  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, { unlocks }, 'PUT');
  return fetch(`${baseUrlV2}/membership/user/${membershipId}/admin/add-unlocks`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.data) {
        return true;
      }
      FourOhOneError(results);
      throw new Error(results.error_description);
    })
    .catch((e) => {
      return Promise.reject(`${e}`);
    });
};

export const discountMinutesToMembership = (membershipId, minutes) => {
  const { adminEmail, adminToken, systemId } = getCredentials();

  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, { minutes }, 'PUT');
  return fetch(
    `${baseUrlV2}/membership/user/${membershipId}/admin/discount-minutes`,
    fetchOps
  )
    .then((res) => res.json())
    .then((results) => {
      if (results.data) {
        return true;
      }
      FourOhOneError(results);
      throw new Error(results.error_description);
    })
    .catch((e) => {
      return Promise.reject(`${e}`);
    });
};

export const discountUnlocksToMembership = (membershipId, unlocks) => {
  const { adminEmail, adminToken, systemId } = getCredentials();

  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, { unlocks }, 'PUT');
  return fetch(
    `${baseUrlV2}/membership/user/${membershipId}/admin/discount-unlocks`,
    fetchOps
  )
    .then((res) => res.json())
    .then((results) => {
      if (results.data) {
        return true;
      }
      FourOhOneError(results);
      throw new Error(results.error_description);
    })
    .catch((e) => {
      return Promise.reject(`${e}`);
    });
};

export const getSystemGoStatus = async (system_id) => {
  const { adminEmail, adminToken, systemGid } = getCredentials();

  const queryParametersObject = {
    system_gid: system_id || systemGid,
  };
  const queryParameters = createQueryParameters(queryParametersObject);
  const fetchOps = fetchOptionsV2(adminEmail, adminToken);
  return fetch(`${baseUrlV2}/membership/perks/system?${queryParameters}`, fetchOps)
    .then((response) => response.json())
    .then((response) => {
      if (response?.data) {
        return response;
      }
      FourOhOneError(response);
      throw new Error(response?.error_description);
    })
    .catch((e) => {
      CustomToast({
        type: 'error',
        message: `Oops! Looks like there was an error while loading the go status. Please try again later. ${e}`,
        duration: 5000,
      });
      return {};
    });
};

export const toggleSystemGoStatus = async (enabled, system_id) => {
  const { adminEmail, adminToken, systemGid } = getCredentials();
  const fetchOps = fetchPostOptions(
    adminEmail,
    adminToken,
    systemGid,
    { system_gid: system_id || systemGid, enabled },
    'POST'
  );
  return fetch(`${baseUrlV2}/membership/perks/system`, fetchOps)
    .then((res) => res.json())
    .then((results) => {
      if (results.data) {
        return results.data;
      }
      FourOhOneError(results);
      throw new Error(results.message);
    })
    .catch((error) => {
      return Promise.reject(`${error}`);
    });
};
